import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { toast } from "react-toastify";
import {
  Download,
  FileText,
  ChevronLeft,
  ChevronRight,
  ArrowLeft,
} from "lucide-react";
import "./InstantLoanEmiDetails.css";

const BASE_URL = "https://payyug.in/api";
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100, "All"];

const InstantLoanEmiDetails = () => {
  const { loanId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const routeLoanData = location.state?.loanData || null;
  const superAdminId = localStorage.getItem("adminuserId") || undefined;

  // Active Tab
  const [activeTab, setActiveTab] = useState("PENDING");
  const [company, setCompany] = useState(null);

  // API Data
  const [loanSummary, setLoanSummary] = useState(routeLoanData);
  const [schedule, setSchedule] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);

  // Verification Modal State
  // const [isVerifyModalOpen, setIsVerifyModalOpen] = useState(false);
  // const [selectedEmi, setSelectedEmi] = useState(null);
  // const [paymentMode, setPaymentMode] = useState("BANK_TRANSFER");
  // const [paymentDate, setPaymentDate] = useState(
  //   new Date().toISOString().split("T")[0]
  // );
  // const [isVerifying, setIsVerifying] = useState(false);

  // Safe Currency Formatter
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || amount === "") return "—";
    const num = Number(amount);
    if (isNaN(num)) return String(amount);

    return `₹${num.toLocaleString("en-IN", {
      maximumFractionDigits: 2,
    })}`;
  };

  // Safe Date Formatter
  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const date = new Date(dateStr);
    return isNaN(date.getTime()) ? "N/A" : date.toLocaleDateString("en-GB");
  };


  useEffect(() => {
    const fetchCompany = async () => {
      try {
        const response = await fetch(
          `${BASE_URL}/get-company?superAdminId=${superAdminId}`
        );
        const data = await response.json();
        if (response.ok && data) {
          if (data.logo) {
            data.logoUrl = `${BASE_URL.replace("/api", "")}/${data.logo.replace(
              /^images\//,
              ""
            )}`;
          }
          setCompany(data);
        }
      } catch (err) {
        console.error("Failed to load company details:", err);
      }
    };

    if (superAdminId) fetchCompany();
  }, [superAdminId]);

  // 1. Fetch EMI Schedule matching response structure
  const fetchLoanEmis = useCallback(async () => {
    if (!loanId) {
      toast.error("Loan ID is required to fetch EMI schedule.");
      return;
    }

    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/instant-loan/application/${loanId}/emi-schedule`, {
        params: {
          page: currentPage,
          limit: itemsPerPage === "All" ? 100000 : itemsPerPage,
          status:activeTab,
        },
      });

      if (res.data?.success) {
        const responseData = res.data.data;
        setLoanSummary((prev) => ({
          ...prev,
          ...responseData,
        }));

        setSchedule(responseData?.schedule || []);
        setTotalPages(res.data.totalPages || 1);
        setTotalRecords(res.data.totalRecords || 0);
        setHasNextPage(Boolean(res.data.hasNextPage));
        setHasPreviousPage(Boolean(res.data.hasPreviousPage));
      } else {
        setSchedule([]);
      }
    } catch (error) {
      console.error("Error fetching EMI schedule:", error);
      toast.error(error.response?.data?.message || "Failed to fetch EMI details.");
    } finally {
      setLoading(false);
    }
  }, [loanId, currentPage, itemsPerPage,activeTab]);

  useEffect(() => {
    fetchLoanEmis();
  }, [fetchLoanEmis]);

  // 2. Tab & Date Filtering
  const filteredSchedule = useMemo(() => {
    const term = searchQuery.trim().toLowerCase();

    return schedule.filter((item) => {
      // 1. Tab check via paymentStatus
      const paymentStatus = String(item.paymentStatus || "PENDING").toUpperCase();
      const isPaid = paymentStatus === "PAID";
      const isPending =
        paymentStatus === "PENDING" ||
        paymentStatus === "OVERDUE" ||
        paymentStatus === "PARTIAL";

      if (activeTab === "PAID" && !isPaid) return false;
      if (activeTab === "PENDING" && !isPending) return false;

      // 2. Date Range Filter
      const dateToCheck =
        activeTab === "PAID" && item.paidDate
          ? String(item.paidDate).slice(0, 10)
          : item.dueDate
          ? String(item.dueDate).slice(0, 10)
          : "";

      if (fromDate && (!dateToCheck || dateToCheck < fromDate)) return false;
      if (toDate && (!dateToCheck || dateToCheck > toDate)) return false;

      // 3. Search Filter
      if (!term) return true;

      const emiNo = String(item.emiNumber || "").toLowerCase();
      const txn = String(item.transactionId || "").toLowerCase();
      const ref = String(item.paymentReference || "").toLowerCase();

      return emiNo.includes(term) || txn.includes(term) || ref.includes(term);
    });
  }, [schedule, activeTab, fromDate, toDate, searchQuery]);

  // 3. Modal Handlers
  // const handleOpenVerifyModal = (item) => {
  //   setSelectedEmi(item);
  //   setPaymentMode("BANK_TRANSFER");
  //   setPaymentDate(new Date().toISOString().split("T")[0]);
  //   setIsVerifyModalOpen(true);
  // };

  // const handleConfirmVerify = async (e) => {
  //   e.preventDefault();
  //   if (!selectedEmi) return;

  //   try {
  //     setIsVerifying(true);
  //     const res = await axios.post(`${BASE_URL}/loan/pay-emi`, {
  //       emiId: selectedEmi.id,
  //       emiNumber: selectedEmi.emiNumber,
  //       superAdminId: Number(superAdminId),
  //       amountPaid: Number(selectedEmi.outstandingAmount || selectedEmi.emiAmount),
  //       paymentMode,
  //       paymentDate,
  //     });

  //     if (res.data?.success || res.status === 200) {
  //       toast.success(res.data?.message || "EMI payment successfully recorded.");
  //       setIsVerifyModalOpen(false);
  //       setSelectedEmi(null);
  //       await fetchLoanEmis(); // Refetch updated records
  //     }
  //   } catch (error) {
  //     toast.error(error.response?.data?.message || "Payment verification failed.");
  //   } finally {
  //     setIsVerifying(false);
  //   }
  // };

  // 4. Export Functions
  const handleDownloadExcel = () => {
    const excelData = filteredSchedule.map((c, index) => ({
      "#": index + 1,
      "EMI Number": c.emiNumber,
      "EMI Amount": c.emiAmount,
      Principal: c.principalAmount,
      Interest: c.interestAmount,
      "Paid Amount": c.paidAmount,
      Outstanding: c.outstandingAmount,
      "Due Date": formatDate(c.dueDate),
      "Paid Date": c.paidDate ? formatDate(c.paidDate) : "—",
      Status: c.paymentStatus,
    }));

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, `${activeTab}_EMIs`);
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, `EMI_Schedule_Loan_${loanId}_${activeTab}.xlsx`);
  };



  const handleDownloadPDF = async () => {
  const doc = new jsPDF("p", "mm", "a4",true);
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;

  const marginX = 14;
  const logoX = marginX;
  const logoY = 12;
  const logoSize = 22;

  // 1. Render Logo if available
  if (company?.logoUrl) {
    try {
      const logoResp = await fetch(company.logoUrl);
      const logoBlob = await logoResp.blob();
      const reader = new FileReader();
      await new Promise((resolve) => {
        reader.onload = () => {
          doc.addImage(reader.result, "PNG", logoX, logoY, logoSize, logoSize);
          resolve();
        };
        reader.readAsDataURL(logoBlob);
      });
    } catch {
      // Continue cleanly without logo
    }
  }

  const titleX = company?.logoUrl ? logoX + logoSize + 6 : marginX;
  const maxHeaderWidth = pageWidth - marginX - titleX;
  let currentY = 16;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  const companyName = (company?.name || "PAYYUG SYSTEM").toUpperCase();
  const nameLines = doc.splitTextToSize(companyName, maxHeaderWidth);
  doc.text(nameLines, titleX, currentY);
  currentY += nameLines.length * 5;

  // Address (Wrapped)
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  const addressText = company?.address || "";
  const addressLines = doc.splitTextToSize(addressText, maxHeaderWidth);
  doc.text(addressLines, titleX, currentY);
  currentY += addressLines.length * 4;

  // Contact & Email (Wrapped)
  const contactText = `Contact: ${company?.contact || "—"} | Email: ${company?.email || "—"}`;
  const contactLines = doc.splitTextToSize(contactText, maxHeaderWidth);
  doc.text(contactLines, titleX, currentY);
  currentY += contactLines.length * 4;

  if (company?.logoUrl && currentY < logoY + logoSize) {
    currentY = logoY + logoSize + 3;
  } else {
    currentY += 2;
  }

  // Divider Line
  doc.setDrawColor(203, 213, 225); 
  doc.setLineWidth(0.2);
  doc.line(marginX, currentY, pageWidth - marginX, currentY);
  currentY += 6;

  // Report Title
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(15, 23, 42);
  doc.text(
    `Instant Loan EMI Schedule (${activeTab})`,
    pageWidth / 2,
    currentY,
    { align: "center" }
  );
  currentY += 4;

  // Table Columns
  const tableColumns = [
    "#",
    "Holder Name",
    "EMI No",
    "EMI Amount",
    "Principal",
    "Interest",
    "Outstanding",
    "Due Date",
    "Paid Date",
    "Status",
  ];

  // Table Rows
  const tableRows = filteredSchedule.map((c, index) => [
    index + 1,
    loanSummary.accountHolderName,
    c.emiNumber,
    c.emiAmount,
    c.principalAmount,
    c.interestAmount,
    c.outstandingAmount,
    formatDate(c.dueDate),
    c.paidDate ? formatDate(c.paidDate) : "—",
    c.paymentStatus,
  ]);

  // 3. Render Table dynamically below header
  autoTable(doc, {
    startY: currentY + 3,
    head: [tableColumns],
    body: tableRows,
    theme: "grid",
    styles: {
      fontSize: 8,
      cellPadding: 2,
      halign: "center",
      valign: "middle",
      overflow: "linebreak",
    },
    headStyles: {
      fillColor: [37, 99, 235],
      textColor: [255, 255, 255],
      fontStyle: "bold",
    },
    alternateRowStyles: {
      fillColor: [248, 250, 252],
    },
    margin: { left: marginX, right: marginX },
  });

  // 4. Page numbering & footer
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(148, 163, 184);
    doc.text(
      "Generated by Instant Loan Management Center",
      marginX,
      pageHeight - 8
    );
    doc.text(
      `Page ${i} of ${pageCount}`,
      pageWidth - marginX - 12,
      pageHeight - 8
    );
  }

  doc.save(`EMI_Schedule_Loan_${loanId}_${activeTab}.pdf`);
};
  return (
    <div className="il-emi-container">
      {/* Back Button */}
      <div className="il-emi-nav-header">
        <button
          type="button"
          className="il-emi-btn-back"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft size={16} /> Back
        </button>
        
      </div>

      {/* Loan Overview Banner */}
      {loanSummary && (
        <div className="il-emi-loan-banner">
          <div className="il-emi-banner-item">
            <label>Application No</label>
            <strong>{loanSummary.applicationNo || `APP-${loanSummary.applicationId || loanId}`}</strong>
          </div>
          <div className="il-emi-banner-item">
            <label>Borrower</label>
            <strong>{loanSummary.accountHolderName || loanSummary.applicantName || "N/A"}</strong>
          </div>
          <div className="il-emi-banner-item">
            <label>Sanctioned Amount</label>
            <strong className="il-emi-highlight-amount">
              {formatCurrency(loanSummary.approvedAmount || loanSummary.loanAmount)}
            </strong>
          </div>
          <div className="il-emi-banner-item">
            <label>Monthly EMI</label>
            <strong>{formatCurrency(loanSummary.emiAmount)}</strong>
          </div>
          <div className="il-emi-banner-item">
            <label>Interest Rate</label>
            <strong>{loanSummary.interestRate}%</strong>
          </div>
          <div className="il-emi-banner-item">
            <label>Tenure</label>
            <strong>{loanSummary.tenure} Months</strong>
          </div>
        </div>
      )}

      {/* Main Card */}
      <div className="il-emi-card">
        {/* Tab Headers */}
        <div className="il-emi-tabs-container">
          <button
            type="button"
            className={`il-emi-tab ${activeTab === "PENDING" ? "active" : ""}`}
            onClick={() => setActiveTab("PENDING")}
          >
            Pending / Due EMIs
          </button>
          <button
            type="button"
            className={`il-emi-tab ${activeTab === "PAID" ? "active" : ""}`}
            onClick={() => setActiveTab("PAID")}
          >
            Paid / Cleared EMIs
          </button>
        </div>

        {/* Search & Date Filter Bar */}
        <div className="il-emi-filter-bar">
          <div className="il-emi-field-group il-emi-search-wrapper">
            <label htmlFor="il-emi-search">Search Schedule</label>
              <input
                id="il-emi-search"
                type="text"
                placeholder="Search ...."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="il-emi-input il-emi-input-search"
              />
          </div>

          <div className="il-emi-field-group">
            <label htmlFor="il-emi-from">From Date</label>
            <input
              id="il-emi-from"
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="il-emi-input"
            />
          </div>

          <div className="il-emi-field-group">
            <label htmlFor="il-emi-to">To Date</label>
            <input
              id="il-emi-to"
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="il-emi-input"
            />
          </div>
        </div>

        {/* Toolbar Controls */}
        <div className="il-emi-toolbar">
          <div className="il-emi-limit-selector">
            <label htmlFor="il-emi-limit">Show:</label>
            <select
              id="il-emi-limit"
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(e.target.value);
                setCurrentPage(1);
              }}
              className="il-emi-select"
            >
              {PAGE_SIZE_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
            <span>entries</span>
          </div>

          {filteredSchedule.length > 0 && (
            <div className="il-emi-export-group">
              <button
                type="button"
                className="il-emi-btn-export"
                onClick={handleDownloadExcel}
              >
                <FileText size={16} /> Excel
              </button>
              <button
                type="button"
                className="il-emi-btn-export"
                onClick={handleDownloadPDF}
              >
                <Download size={16} /> PDF
              </button>
            </div>
          )}
        </div>

        {/* Schedule Table */}
        <div className="il-emi-table-responsive">
          <table className="il-emi-table">
            <thead>
              <tr>
                <th>#</th>
                <th>EMI No</th>
                <th>EMI Amount</th>
                <th>Principal</th>
                <th>Interest</th>
                <th>Opening Bal</th>
                <th>Outstanding</th>
                <th>Due Date</th>
                <th>Paid Date</th>
                <th>Status</th>
                {/* {activeTab === "PENDING" && (
                  <th style={{ textAlign: "center" }}>Action</th>
                )} */}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan={activeTab === "PENDING" ? 11 : 10}
                    className="il-emi-empty-cell"
                  >
                    Loading EMI schedule...
                  </td>
                </tr>
              ) : filteredSchedule.length === 0 ? (
                <tr>
                  <td
                    colSpan={activeTab === "PENDING" ? 11 : 10}
                    className="il-emi-empty-cell"
                  >
                    No {activeTab.toLowerCase()} EMIs found for this selection.
                  </td>
                </tr>
              ) : (
                filteredSchedule.map((emi, idx) => {
                  const statusKey = String(emi.paymentStatus || "").toUpperCase();

                  return (
                    <tr key={emi.id || idx}>
                      <td>{(currentPage - 1) * (itemsPerPage === "All" ? totalRecords : Number(itemsPerPage)) + idx + 1}</td>
                      <td>
                        <span className="il-emi-badge-id">
                          EMI #{emi.emiNumber}
                        </span>
                      </td>
                      <td className="il-emi-amount">{formatCurrency(emi.emiAmount)}</td>
                      <td className="il-emi-amount">{formatCurrency(emi.principalAmount)}</td>
                      <td className="il-emi-amount">{formatCurrency(emi.interestAmount)}</td>
                      <td>{formatCurrency(emi.openingBalance)}</td>
                      <td className="il-emi-amount" style={{ color: "#dc2626" }}>
                        {formatCurrency(emi.outstandingAmount)}
                      </td>
                      <td>{formatDate(emi.dueDate)}</td>
                      <td>{emi.paidDate ? formatDate(emi.paidDate) : "—"}</td>
                      <td>
                        <span
                          className={`il-emi-status-badge ${
                            statusKey === "PAID"
                              ? "il-emi-status-paid"
                              : "il-emi-status-pending"
                          }`}
                        >
                          {emi.paymentStatus}
                        </span>
                      </td>
                      {/* {activeTab === "PENDING" && (
                        <td style={{ textAlign: "center" }}>
                          <button
                            type="button"
                            className="il-emi-btn-verify"
                            onClick={() => handleOpenVerifyModal(emi)}
                          >
                            Verify & Pay
                          </button>
                        </td>
                      )} */}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Footer */}
        {!loading && totalRecords > 0 && (
          <div className="il-emi-pagination-footer">
            <div className="il-emi-pagination-info">
              Showing {(currentPage - 1) * (itemsPerPage === "All" ? totalRecords : Number(itemsPerPage)) + 1} to{" "}
              {itemsPerPage === "All" ? totalRecords : Math.min(currentPage * Number(itemsPerPage), totalRecords)}{" "}
              of {totalRecords} entries
            </div>

            <div className="il-emi-pagination-nav">
              <button
                type="button"
                className="il-emi-nav-btn"
                onClick={() => setCurrentPage((p) => p - 1)}
                disabled={!hasPreviousPage || currentPage === 1 || itemsPerPage === "All"}
              >
                <ChevronLeft size={16} /> Prev
              </button>
              <span className="il-emi-page-counter">
                Page {currentPage} of {totalPages}
              </span>
              <button
                type="button"
                className="il-emi-nav-btn"
                onClick={() => setCurrentPage((p) => p + 1)}
                disabled={!hasNextPage || currentPage >= totalPages || itemsPerPage === "All"}
              >
                Next <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Verification Modal */}
      {/* {isVerifyModalOpen && selectedEmi && (
        <div
          className="il-emi-modal-backdrop"
          onClick={() => !isVerifying && setIsVerifyModalOpen(false)}
        >
          <div className="il-emi-modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="il-emi-modal-head">
              <h3>Verify & Pay Instant EMI</h3>
              <button
                type="button"
                className="il-emi-modal-close"
                disabled={isVerifying}
                onClick={() => setIsVerifyModalOpen(false)}
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleConfirmVerify} className="il-emi-modal-form">
              <div className="il-emi-modal-body">
                <div className="il-emi-summary-box">
                  <div>
                    <label>Borrower</label>
                    <p>{loanSummary?.accountHolderName || loanSummary?.applicantName || "N/A"}</p>
                  </div>
                  <div>
                    <label>EMI Number</label>
                    <p>#{selectedEmi.emiNumber}</p>
                  </div>
                  <div>
                    <label>Due Amount</label>
                    <p className="il-emi-highlight-amount">
                      {formatCurrency(selectedEmi.outstandingAmount || selectedEmi.emiAmount)}
                    </p>
                  </div>
                  <div>
                    <label>Due Date</label>
                    <p>{formatDate(selectedEmi.dueDate)}</p>
                  </div>
                </div>

                <div className="il-emi-form-group">
                  <label htmlFor="il-modal-paymode">Payment Mode *</label>
                  <select
                    id="il-modal-paymode"
                    className="il-emi-input"
                    value={paymentMode}
                    disabled={isVerifying}
                    onChange={(e) => setPaymentMode(e.target.value)}
                  >
                    <option value="BANK_TRANSFER">Bank Transfer / IMPS</option>
                    <option value="UPI">UPI / QR Code</option>
                    <option value="CASH">Cash Deposit</option>
                    <option value="CHEQUE">Cheque</option>
                    <option value="NEFT">NEFT</option>
                    <option value="RTGS">RTGS</option>
                  </select>
                </div>

                <div className="il-emi-form-group">
                  <label htmlFor="il-modal-paydate">Payment Date *</label>
                  <input
                    id="il-modal-paydate"
                    type="date"
                    className="il-emi-input"
                    value={paymentDate}
                    disabled={isVerifying}
                    onChange={(e) => setPaymentDate(e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="il-emi-modal-foot">
                <button
                  type="button"
                  className="il-emi-btn-cancel"
                  disabled={isVerifying}
                  onClick={() => setIsVerifyModalOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="il-emi-btn-confirm"
                  disabled={isVerifying}
                >
                  {isVerifying ? "Processing..." : "Confirm & Mark Paid"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )} */}
    </div>
  );
};

export default InstantLoanEmiDetails;